import { useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate, Navigate } from "react-router-dom";
import { LogOut, Menu, X, Lock, KeyRound, Search, ChevronDown, ChevronsLeft, ChevronsRight, UserCircle } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { useAuth } from "@/context/AuthContext";
import { MODULES, NAV_GROUPS } from "@/constants/crm";
import { FinanceLockProvider } from "./FinanceLock";
import { ChangePasswordDialog } from "./ChangePasswordDialog";
import { NotificationCentre } from "./NotificationCentre";
import { ProfileDialog } from "./ProfileDialog";
import { Avatar3D } from "./UserImage";

function NavItem({ m, onClick, collapsed }) {
  const Icon = m.icon;
  return (
    <NavLink
      to={m.path}
      end={m.path === "/"}
      onClick={onClick}
      data-testid={`nav-module-${m.num.toLowerCase()}-${m.slug}`}
      title={collapsed ? m.label : undefined}
      className={({ isActive }) =>
        `flex h-9 items-center gap-3 rounded-lg ${collapsed ? "justify-center px-0" : "px-3"} text-sm transition-colors ${
          isActive ? "!bg-[#8B7356] font-bold !text-[#F8F4EE]" : "font-semibold !text-[#F8F4EE]/80 hover:!bg-white/5 hover:!text-[#D6C6B0]"
        }`
      }
    >
      <span className="icon-3d"><Icon className="h-[18px] w-[18px] shrink-0" strokeWidth={1.9} /></span>
      {!collapsed && <span className="truncate">{m.label}</span>}
    </NavLink>
  );
}

function Sidebar({ onNav, collapsed, onToggle }) {
  const { can } = useAuth();
  return (
    <aside data-testid="sidebar" className={`flex h-full ${collapsed ? "w-[72px]" : "w-[248px]"} flex-col !border-r-0 !bg-[#292820] transition-[width] duration-200`}>
      <div className={`flex h-[72px] items-center gap-3 ${collapsed ? "justify-center px-2" : "px-5"}`}>
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg !bg-[#8B7356] font-display text-lg font-extrabold leading-none !text-[#F8F4EE]">JOV</span>
        {!collapsed && (
          <div className="leading-tight">
            <div className="text-sm font-extrabold tracking-wide !text-[#F8F4EE]">JOV Event</div>
            <div className="text-xs font-semibold !text-[#D6C6B0]">Event CRM</div>
          </div>
        )}
      </div>
      {onToggle && <button data-testid="sidebar-toggle" onClick={onToggle} title={collapsed ? "Expand menu" : "Collapse menu"} className="mx-auto mb-1 flex h-7 w-7 items-center justify-center rounded-md !text-[#D6C6B0] hover:!bg-white/5">{collapsed ? <ChevronsRight className="h-4 w-4" /> : <ChevronsLeft className="h-4 w-4" />}</button>}
      <nav className={`thin-scroll flex-1 space-y-1 overflow-y-auto ${collapsed ? "px-2" : "px-4"} py-3`}>
        {NAV_GROUPS.map(([label, nums]) => {
          const mods = nums.map((n) => MODULES.find((m) => m.num === n)).filter((m) => (m.keys || [m.key]).some((k) => can(k)));
          if (!mods.length) return null;
          return (
            <div key={label} className="pb-2">
              {!collapsed && <div className="px-3 pb-1.5 pt-2 text-[10px] font-bold uppercase tracking-[0.14em] !text-[#D6C6B0]/70">{label}</div>}
              {mods.map((m) => <NavItem key={m.num} m={m} onClick={onNav} collapsed={collapsed} />)}
            </div>
          );
        })}
      </nav>
    </aside>
  );
}


function UserMenu({ user, onPassword, onProfile, onLogout }) {
  const roleLabel = user.role === "admin" ? "Administrator" : user.role_name;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button data-testid="user-menu-button" className="flex items-center gap-3 rounded-full py-1 pl-1 pr-2 transition-colors hover:bg-zinc-50">
          <Avatar3D user={user} size={36} testid="header-avatar" />
          <div className="hidden text-left sm:block">
            <div data-testid="header-user-name" className="text-sm font-semibold text-zinc-900">{user.name}</div>
            <div data-testid="header-user-role" className="text-xs text-zinc-500">{roleLabel}</div>
          </div>
          <ChevronDown className="hidden h-4 w-4 text-zinc-400 sm:block" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel className="text-xs font-normal text-zinc-500">{user.email}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem data-testid="my-profile-button" onClick={onProfile}><UserCircle className="mr-2 h-4 w-4" />My profile & alerts</DropdownMenuItem>
        <DropdownMenuItem data-testid="change-password-button" onClick={onPassword}><KeyRound className="mr-2 h-4 w-4" />Change password</DropdownMenuItem>
        <DropdownMenuItem data-testid="logout-button" onClick={onLogout} className="text-rose-600"><LogOut className="mr-2 h-4 w-4" />Log out</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default function Layout() {
  const { user, logout, can } = useAuth();
  const [mobile, setMobile] = useState(false);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("jov-sidebar") === "collapsed");
  const toggle = () => setCollapsed((c) => { localStorage.setItem("jov-sidebar", c ? "open" : "collapsed"); return !c; });
  const [pwOpen, setPwOpen] = useState(false);
  const [profOpen, setProfOpen] = useState(false);
  const [q, setQ] = useState("");
  const { pathname } = useLocation();
  const nav = useNavigate();
  if (user === null) return <div className="flex h-screen items-center justify-center text-sm text-zinc-400">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  const mod = MODULES.find((m) => (m.path === "/" ? pathname === "/" : pathname.startsWith(m.path)));
  const blocked = mod?.key && !(mod.keys || [mod.key]).some((k) => can(k));
  const search = (e) => { e.preventDefault(); if (q.trim()) nav(`/projects?q=${encodeURIComponent(q.trim())}`); };

  return (
    <div className="flex h-screen overflow-hidden bg-[#FAF9F6]">
      <div className="hidden md:block"><Sidebar collapsed={collapsed} onToggle={toggle} /></div>
      {mobile && (
        <div className="fixed inset-0 z-50 flex md:hidden">
          <Sidebar onNav={() => setMobile(false)} />
          <button data-testid="mobile-nav-close" className="flex-1 bg-zinc-900/30 backdrop-blur-sm" onClick={() => setMobile(false)}><X className="m-4 h-5 w-5 text-white" /></button>
        </div>
      )}
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex h-[72px] shrink-0 items-center justify-between gap-4 border-b border-zinc-100 bg-white px-4 md:px-8">
          <div className="flex min-w-0 flex-1 items-center gap-3">
            <button data-testid="mobile-nav-open" className="md:hidden" onClick={() => setMobile(true)}><Menu className="h-5 w-5" /></button>
            <form onSubmit={search} className="relative w-full max-w-md">
              <Search className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
              <input data-testid="global-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search projects, clients…"
                className="h-10 w-full rounded-lg border border-transparent bg-zinc-50 pl-10 pr-3 text-sm placeholder:text-zinc-400 transition-colors focus:border-violet-300 focus:bg-white focus:outline-none" />
            </form>
          </div>
          <div className="flex items-center gap-2 sm:gap-4">
            <NotificationCentre />
            <UserMenu user={user} onPassword={() => setPwOpen(true)} onProfile={() => setProfOpen(true)} onLogout={logout} />
          </div>
        </header>
        <main className="ivory-canvas thin-scroll min-w-0 flex-1 overflow-y-auto overflow-x-clip">
          <div aria-hidden="true" className="ivory-ornament" />
          <div className="mx-auto w-full max-w-[1480px] space-y-6 p-4 md:p-7">
            {blocked ? (
              <div data-testid="no-access" className="flex flex-col items-center justify-center rounded-2xl border border-zinc-100 bg-white py-24 text-center">
                <Lock className="h-7 w-7 text-zinc-300" />
                <div className="mt-3 text-base font-semibold text-zinc-900">No access to {mod.label}</div>
                <div className="mt-1 text-sm text-zinc-500">Your role does not include this page. Ask an administrator to update Roles & Permissions.</div>
              </div>
            ) : <FinanceLockProvider><Outlet /></FinanceLockProvider>}
          </div>
        </main>
        <ProfileDialog open={profOpen} onOpenChange={setProfOpen} onPassword={() => setPwOpen(true)} />
        <ChangePasswordDialog open={pwOpen || !!user.must_change_password} onOpenChange={setPwOpen} forced={!!user.must_change_password} />
      </div>
    </div>
  );
}
